// When the 3D view gives up, what happens to the player's saved view.
//
// 3D is the default, so a fallback that wrote `renderer: 'canvas2d'` into the
// settings quietly moved a player to 2D for good over one bad moment (QA
// phase 2: the 17th job in a session lost its context and flipped the save).
// The rule now: a fallback shows a note and runs this job in 2D, and the save
// is left alone. The one exception is the phone's first 3D job running under
// the low-fps line: that is a verdict on the phone, so the view moves to 2D,
// but unchosen (rendererChosen: false), so the next launch tries 3D again
// (parseSettings moves an unchosen view to 3D) rather than locking it in.
//
// Pure, so the rule is unit-tested (tests/game/view-fallback.test.ts).

import type { Settings } from './store';

export type FallbackReason =
  /** WebGL unavailable, or the three.js chunk didn't load. */
  | 'mount-failed'
  /** The GPU dropped the context mid-job and it didn't come back. */
  | 'context-lost'
  /** The phone's first 3D job ran under the low-fps line (JobPage LOW_FPS). */
  | 'low-fps';

/** The settings patch a 3D fallback writes, or null to leave the save alone. */
export function fallbackSettings(reason: FallbackReason): Pick<Settings, 'renderer' | 'rendererChosen'> | null {
  return reason === 'low-fps' ? { renderer: 'canvas2d', rendererChosen: false } : null;
}

export interface LossContext {
  /** We caused it: unmount()'s forceContextLoss, or a renderer already being dropped. */
  ours: boolean;
  /** The job page is the one on screen (not hidden in the stack, not leaving). */
  inView: boolean;
}

/**
 * A webglcontextlost on the job page: `ignore` ours (unmount) and a page that
 * isn't on screen (it gets a fresh renderer when it is); otherwise `wait` for
 * the browser to restore the context (see afterRestoreWait).
 */
export function onContextLoss(c: LossContext): 'ignore' | 'wait' {
  return c.ours || !c.inView ? 'ignore' : 'wait';
}

/**
 * The wait after a genuine loss is over. Restored: carry on in 3D (three.js
 * re-uploads everything). Not restored: re-create the 3D view, once per job;
 * if this job already had its re-create, 2D with a note (the save untouched).
 */
export function afterRestoreWait(s: { restored: boolean; recreated: boolean }): 'carry-on' | 'recreate' | 'fallback' {
  if (s.restored) return 'carry-on';
  return s.recreated ? 'fallback' : 'recreate';
}

/**
 * 3D wouldn't start at all this session (no WebGL, the chunk didn't load):
 * later jobs open straight in 2D without the note, until the player picks 3D.
 */
export const session3d = { mountFailed: false };

/** The view to show: the saved one, unless this job (or session) had to fall back to 2D. */
export function viewToShow(saved: Settings['renderer'], forced2d: boolean): Settings['renderer'] {
  return forced2d ? 'canvas2d' : saved;
}
